<script setup lang="ts">
import {onMounted, ref} from 'vue';
import ZZC from '@/views/header/ZZCLogo.vue';
import {getAppVersion} from '@/base/frontendConfig';
import {tokenControle, wachtwoordZetten} from '@/base/auth.js';
import {WachtwoordAntwoord} from 'common';

type ReportState = { error: boolean, text?: string, next?: string };

// the mail puts the token in the fragment, so it never reaches a server log; the '#' itself is stripped first
const token: string = new URLSearchParams(window.location.hash.substring(1)).get('token') ?? '';
const newPassword   = ref<string>('');
const reportState   = ref<ReportState>(makeOk());
const linkGeldig    = ref<boolean | undefined>(undefined); // undefined while the link is being checked
let errorTimerId: number | ReturnType<typeof setTimeout>;

// a dead link is told on arrival, before a password is typed; expired, used and made-up links all read the same
onMounted(async () => {
    linkGeldig.value = await tokenControle(token);
});

function makeError(text: string, next?: string): ReportState {
    return {
        error: true,
        text,
        next,
    };
}

function makeOk(text?: string, next?: string): ReportState {
    return {
        error: false,
        text,
        next,
    };
}

function setReportState(state: ReportState) {
    clrReportState();
    reportState.value = state;
    if (state.text) {
        errorTimerId = setTimeout(clrReportState, 5000);
    } else {
        clrReportState();
    }
}

function clrReportState() {
    if (reportState.value.next) {
        window.location.assign(reportState.value.next);
    }
    reportState.value = makeOk();
    if (errorTimerId) {
        clearTimeout(errorTimerId);
        errorTimerId = 0;
    }
}

async function onResetPassword() {
    setReportState(await resetPassword());
}

async function resetPassword(): Promise<ReportState> {
    const antwoord: WachtwoordAntwoord = await wachtwoordZetten(token, newPassword.value);
    switch (antwoord.status) {
        case 'ok':
            return makeOk('Je wachtwoord is gezet', '/');
        case 'beleid':
            return makeError(antwoord.message ?? 'dit wachtwoord voldoet niet');
        case 'ongeldig':
            return makeError('ongeldig token', '/');
        default:
            console.error(antwoord);
            return makeError('er is een fout opgetreden', '/');
    }
}
</script>

<template>
  <div class="z-50 absolute top-[12px] left-[12px] px-2 text-xs text-center">
    {{ getAppVersion() }}
  </div>
  <div class="min-h-screen flex items-center justify-center">
    <div
        @keydown.enter="onResetPassword"
        class="relative flex flex-col w-[440px] mx-auto p-8 bg-white rounded-lg shadow-2xl between space-y-4"
    >
      <ZZC/>
      <div class="text-2xl font-bold text-center">
        Wachtwoord kiezen
      </div>

      <div v-if="linkGeldig === false" class="flex flex-col items-center space-y-4" data-testid="link-ongeldig">
        <div class="py-1 px-2 text-white bg-red-600">
          ongeldig token
        </div>
        <div class="text-sm text-center">
          Vraag op de inlogpagina een nieuwe aan met "Wachtwoord reset".
        </div>
        <a href="/" class="text-blue-700 underline">naar de inlogpagina</a>
      </div>
      <div v-if="linkGeldig">
        <input
            id="newpassword"
            v-model="newPassword"
            type="password"
            class="mt-1 px-3 py-1 block w-full rounded-md shadow-sm border border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
        />
      </div>
      <div v-if="linkGeldig" class="flex flex-row justify-center items-center w-full mt-3 h-8">
        <Transition
            enter-from-class="opacity-0"
            enter-active-class="transition-opacity duration-300 ease-out"
            enter-to-class="opacity-100"
            leave-from-class="opacity-100"
            leave-active-class="transition-opacity duration-1000 ease-in"
            leave-to-class="opacity-0"
        >
          <div v-if="reportState.text" class="py-1 px-2 text-white"
               :class="reportState.error?'bg-red-600':'bg-green-600'">
            {{ reportState.text }}
          </div>
        </Transition>
      </div>
      <div v-if="linkGeldig" class="flex items-center">
        <button
            @click="onResetPassword"
            class="w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded cursor-pointer"
        >
          Opslaan
        </button>
      </div>
    </div>
  </div>
</template>